// DTO ⇄ view model for lib/api/unitsApi.js. build… = to server, process… = to screen.

// Enum → symbol for the unit fields the server sends unmapped; mirrors UNIT_MAPPING in iscc-backend.
const UNIT_SYMBOLS = Object.freeze({
  CUBIC_METER: 'm³',
  CUBIC_METER_15C: 'm³/15°C',
  EQUIVALENT: 'Eq.',
});

export const unitSymbol = (unit) => UNIT_SYMBOLS[unit] ?? unit ?? '';

const toPositiveNumber = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

// Whether the material tracks a second unit — i.e. whether a working/declaration correlation is set in account setup.
export const hasWorkingUnit = (material) => Boolean(material?.workingUnit);

// Whether the material fixes the density, making the field read-only.
export const hasMaterialDensity = (material) => toPositiveNumber(material?.density) != null;

// The material's units as options, working first; omits Working unit entirely when the material has no correlation. `labels` is `{ working, declaration }`.
export const buildUnitOptions = (material, labels) => {
  if (!material) return [];
  const units = [
    { unit: material.workingUnit, badgeText: labels.working },
    { unit: material.declarationUnit, badgeText: labels.declaration },
  ];
  return units
    .filter((entry) => Boolean(entry.unit))
    .map((entry) => ({ value: entry.unit, label: entry.unit, badgeText: entry.badgeText }));
};

// The unit a density is measured against — the material's other one. Used for the payload.
export const resolveEquivalentUnit = ({ unit, material }) => {
  if (!material || !unit) return null;
  if (unit === material.workingUnit) return material.declarationUnit || null;
  if (unit === material.declarationUnit) return material.workingUnit || null;
  return null;
};

// Keeps a null out of a controlled input.
export const quantityText = (value) => (value == null ? '' : String(value));

/**
 * One conversion request covering every unit the material is tracked in. The server
 * takes exactly ONE quantity and answers with all three.
 *
 * @param material  the material view model (declaration + working units)
 * @param unit       the unit `quantity` was entered in
 * @param quantity   the entered amount
 * @param density    the declaration ⇄ working factor, when the pair needs one
 * @param trackingUnit     the third unit, when one is configured
 * @param trackingDensity  the declaration ⇄ tracking factor, when that pair needs one
 */
export const buildUnitConversionPayload = ({
  material,
  unit,
  quantity,
  density,
  trackingUnit,
  trackingDensity,
}) => {
  const enteredIn = (candidate) => Boolean(unit) && unit === candidate;
  return {
    declarationUnit: material.declarationUnit,
    workingUnit: material.workingUnit || undefined,
    trackingUnit: trackingUnit || undefined,
    conversionFactorForWorking: density || undefined,
    conversionFactorForTracking: trackingDensity || undefined,
    // Exactly one of the three; the entered unit decides which leg it is.
    declarationQty: enteredIn(material.declarationUnit) ? quantity : undefined,
    workingQty: enteredIn(material.workingUnit) ? quantity : undefined,
    trackingQty: enteredIn(trackingUnit) ? quantity : undefined,
  };
};

// The three quantities plus the factors the server actually used.
export const processUnitConversion = (dto) => {
  const data = dto.output?.data;
  if (!data) return null;
  return {
    declarationQuantity: data.declarationUnitQty ?? null,
    workingQuantity: data.workingUnitQty ?? null,
    trackingQuantity: data.trackingUnitQty ?? null,
    workingFactor: data.conversionFactorForWorking ?? null,
    trackingFactor: data.conversionFactorForTracking ?? null,
  };
};

/**
 * The Add/Edit forms' read-only quantity columns, one per unit the row is tracked in. The
 * column the user typed into mirrors the entry; the rest come from the conversion.
 *
 * @param entered      the quantity as entered, verbatim — the caller formats it
 * @param unit         the unit that quantity was entered in
 * @param material     the selected material view model
 * @param trackingUnit the third unit, when one is configured
 * @param conversion   a processUnitConversion result, or null
 */
export const resolveQuantityColumns = ({ entered, unit, material, trackingUnit, conversion }) => {
  const enteredIn = (candidate) => Boolean(unit) && unit === candidate;
  return {
    declarationQuantity: enteredIn(material?.declarationUnit)
      ? entered
      : (conversion?.declarationQuantity ?? null),
    workingQuantity: enteredIn(material?.workingUnit)
      ? entered
      : (conversion?.workingQuantity ?? null),
    trackingQuantity: enteredIn(trackingUnit) ? entered : (conversion?.trackingQuantity ?? null),
  };
};
